import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { adminOverview } from "@/lib/admin.functions";
import { ShareChartOnX } from "@/components/admin/share-chart-x";
import { formatHeat, formatMoney, formatUsd } from "@/lib/behot";
import { privatePageMetadata } from "@/lib/seo";

export const Route = createFileRoute("/nc/live/adm/")({
  head: () =>
    privatePageMetadata("/nc/live/adm", "Dashboard | Control room", "Internal administration."),
  component: AdminDashboard,
});

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="card-surface p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-2xl font-bold">{value}</p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function AdminDashboard() {
  const fetchOverview = useServerFn(adminOverview);
  const { data, isLoading } = useQuery({
    queryKey: ["adm-overview"],
    queryFn: () => fetchOverview({ data: undefined }),
  });

  if (isLoading || !data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const s = data.stats;

  return (
    <div className="space-y-8">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Revenue (all time)" value={formatMoney(s.revenueCents)} hint={formatUsd(s.revenueCents)} />
        <Stat label="Revenue (30 days)" value={formatMoney(s.revenue30Cents)} hint={`${s.paidOrders} paid orders`} />
        <Stat label="Active heat" value={`${formatHeat(s.activeHeatCents)} Heat`} hint={`${s.activeBoosts} active boosts`} />
        <Stat label="Users" value={String(s.users)} hint={`${s.proUsers} pro · ${s.suspendedUsers} suspended`} />
        <Stat label="Listings" value={String(s.listings)} hint={`${s.pendingListings} awaiting review`} />
        <Stat label="Orders" value={String(s.orders)} />
        <Stat
          label="Pending payouts"
          value={String(s.pendingWithdrawals)}
          hint={formatMoney(s.pendingWithdrawalCents)}
        />
        <Stat label="Waitlist" value={String(s.waitlist)} />
      </div>

      <div className="flex flex-wrap gap-2">
        <Link to="/nc/live/adm/listings" className="rounded-lg border px-3 py-2 text-sm hover:bg-muted">
          Moderate listings ({s.pendingListings})
        </Link>
        <Link to="/nc/live/adm/finance" className="rounded-lg border px-3 py-2 text-sm hover:bg-muted">
          Payouts ({s.pendingWithdrawals})
        </Link>
        <Link to="/nc/live/adm/settings" className="rounded-lg border px-3 py-2 text-sm hover:bg-muted">
          Maintenance mode
        </Link>
      </div>

      <ShareChartOnX />

      <section>
        <h2 className="font-display text-lg font-bold">Recent admin activity</h2>
        <div className="mt-3 overflow-x-auto rounded-xl border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="p-3">When</th>
                <th className="p-3">Action</th>
                <th className="p-3">Target</th>
              </tr>
            </thead>
            <tbody>
              {data.audit.length === 0 && (
                <tr>
                  <td className="p-3 text-muted-foreground" colSpan={3}>
                    No activity yet.
                  </td>
                </tr>
              )}
              {data.audit.map((a) => (
                <tr key={a.id} className="border-t">
                  <td className="p-3 whitespace-nowrap text-muted-foreground">
                    {new Date(a.created_at).toLocaleString()}
                  </td>
                  <td className="p-3 font-medium">{a.action}</td>
                  <td className="p-3 text-muted-foreground">
                    {a.target_type ?? "—"} {a.target_id ? a.target_id.slice(0, 8) : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
